import * as React from 'react'
import {Divider, Menu, Typography, MenuItem, ListItemIcon, ListItemText, CircularProgress} from '@mui/material'
import {useOcAppBarState} from '../../Contexts/OcAppBarStateContext'
import {observer} from 'mobx-react-lite'
import {AccountCircle} from '@mui/icons-material'
import {makeOcStyles} from '@oc/styles'
import {useDateTimezoneState} from '@oc/shell-core'
import {ocAppBarActions, OcAppBarActionContext} from './OcAppBarActions'
import {useLogout} from '../../Hooks/useLogout'
import {PlantIcon} from '@oc/icons'
import {ProfilePictureButton, ProfilePictureEditButton} from './ProfilePictureButtons'
import {OcAppBarHmiStudioSubMenu} from './OcAppBarHmiStudioSubMenu'

const useOCAppBarMenuStyles = makeOcStyles(theme => ({
    rootDiv: {
        display: 'flex',
        alignItems: 'center'
    },
    menu: {
        paddingTop: 0.2
    },
    menuIcon: {
        minWidth: theme.spacing(4)
    },
    menuItem: {
        paddingLeft: theme.spacing(3),
        color: theme.syskronPalette.colors.P700
    },
    iconInMenu: {
        color: theme.syskronPalette.colors.P700,
        width: theme.spacing(4),
        height: theme.spacing(4)
    },
    divProfile: {
        height: '150px',
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    profileName: {
        marginTop: theme.spacing(2)
    }
}))

interface OcAppBarMenuProps {
    includeTimeZoneSettings: boolean
    includeEcosystemLinks: boolean
}

function _OcAppBarMenu(props: OcAppBarMenuProps) {
    const {includeEcosystemLinks, includeTimeZoneSettings} = props
    const {classes} = useOCAppBarMenuStyles()
    const state = useOcAppBarState()
    const dateTimezoneState = useDateTimezoneState()

    const [anchorHmiStudioEl, setAnchorHmiStudioEl] = React.useState<Element | null>(null)

    const handleHmiStudioMenuClick = React.useCallback(
        (event: React.MouseEvent) => {
            setAnchorHmiStudioEl(event.currentTarget)
        },
        [setAnchorHmiStudioEl]
    )

    const logoutAction = ocAppBarActions.find(i => i.renderContext === OcAppBarActionContext.logout)
    const logout = useLogout()

    const timeSettingsAction = ocAppBarActions.find(i => i.renderContext === OcAppBarActionContext.timeSettings)

    const [anchorEl, setAnchorEl] = React.useState<Element | null>(null)

    const handleMenuClick = React.useCallback(
        (event: React.MouseEvent) => {
            setAnchorEl(event.currentTarget)
        },
        [setAnchorEl]
    )

    const handleClose = React.useCallback(() => {
        setAnchorEl(null)
    }, [setAnchorEl])

    const handleCustemerTimeZoneSelection = React.useCallback(
        (value: boolean) => {
            handleClose()
            dateTimezoneState.customerTimezoneSelected = value
        },
        [dateTimezoneState, handleClose]
    )

    return (
        <div className={classes.rootDiv}>
            {state.userInfoLoaded ? (
                <ProfilePictureButton onClick={handleMenuClick} data-cy="app-bar-menu-icon" />
            ) : (
                <CircularProgress />
            )}
            <Menu
                data-cy="oc-app-bar-menu"
                anchorEl={anchorEl}
                autoFocus={false}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                classes={{list: classes.menu}}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <div className={classes.divProfile}>
                    <ProfilePictureEditButton closeMenu={handleClose} data-cy="app-bar-profile-picture-edit-button" />
                    <Typography
                        className={classes.profileName}
                    >{`${state.loggedUser?.givenName} ${state.loggedUser?.familyName}`}</Typography>
                </div>

                {/* Global actions */}
                <Divider />
                {ocAppBarActions
                    .filter(i => i.renderContext === OcAppBarActionContext.global)
                    .map(value => (
                        <MenuItem
                            disabled={value.disabled(state)}
                            key={value.id}
                            onClick={event => {
                                if (value.id === 'hmi-studio') {
                                    handleHmiStudioMenuClick(event)
                                } else {
                                    handleClose()
                                }
                                value.onClick(state)
                            }}
                            data-cy={`app-bar-menu-${value.id}`}
                        >
                            <ListItemIcon className={classes.menuIcon}>{value.ocIcon(state)}</ListItemIcon>
                            <ListItemText className={classes.menuItem}>{value.text(state)}</ListItemText>
                        </MenuItem>
                    ))}

                {/* Ecosystem Links - displayed only if view is too small */}
                {state.s2aAccount && includeEcosystemLinks ? <Divider /> : null}
                {state.s2aAccount && includeEcosystemLinks
                    ? ocAppBarActions
                          .filter(i => i.renderContext === OcAppBarActionContext.s2aLinks)
                          .map(value => (
                              <MenuItem
                                  disabled={value.disabled(state)}
                                  key={value.id}
                                  onClick={event => {
                                      if (value.id === 'hmi-studio') {
                                          handleHmiStudioMenuClick(event)
                                      } else {
                                          handleClose()
                                      }
                                      value.onClick(state)
                                  }}
                              >
                                  <ListItemIcon className={classes.menuIcon}>{value.ocIcon(state)}</ListItemIcon>
                                  <ListItemText className={classes.menuItem}>{value.text(state)}</ListItemText>
                              </MenuItem>
                          ))
                    : null}

                {/* Timezone settings - displayed only if view is too small */}
                {includeTimeZoneSettings && timeSettingsAction ? (
                    <div key={`oc-app-bar-menu-${timeSettingsAction.id}`}>
                        <Divider />
                        <MenuItem
                            key={`oc-app-bar-menu-${timeSettingsAction.id}-customer`}
                            onClick={state.s2aAccount ? () => handleCustemerTimeZoneSelection(false) : undefined}
                        >
                            <ListItemIcon className={classes.menuIcon}>
                                {/* TODO use icon from @oc/icons when available */}
                                <AccountCircle className={classes.iconInMenu} />
                            </ListItemIcon>
                            <Typography className={classes.menuItem} variant="body1" data-cy="local-current-time">
                                {!dateTimezoneState.customerTimezoneSelected || !state.s2aAccount ? (
                                    <strong data-cy="local-current-time-strong">{state.localTimezoneTime}</strong>
                                ) : (
                                    state.localTimezoneTime
                                )}
                            </Typography>
                        </MenuItem>
                        <MenuItem
                            key={`oc-app-bar-menu-${timeSettingsAction.id}-plant`}
                            onClick={state.s2aAccount ? () => handleCustemerTimeZoneSelection(true) : undefined}
                        >
                            <ListItemIcon className={classes.menuIcon}>
                                <PlantIcon size="32px" />
                            </ListItemIcon>
                            <Typography className={classes.menuItem} variant="body1" data-cy="customer-current-time">
                                {dateTimezoneState.customerTimezoneSelected && state.s2aAccount ? (
                                    <strong data-cy="customer-current-time-strong">
                                        {state.s2aAccount ? state.customerTimezoneTime : '-'}
                                    </strong>
                                ) : state.s2aAccount ? (
                                    state.customerTimezoneTime
                                ) : (
                                    '-'
                                )}
                            </Typography>
                        </MenuItem>
                    </div>
                ) : null}

                <Divider />
                {logoutAction ? (
                    <MenuItem
                        disabled={logoutAction.disabled(state)}
                        key={logoutAction.id}
                        onClick={logoutAction.id === 'logout' ? logout : () => logoutAction.onClick(state)}
                    >
                        <ListItemIcon className={classes.menuIcon}>{logoutAction.ocIcon(state)}</ListItemIcon>
                        <ListItemText className={classes.menuItem}>{logoutAction.text(state)}</ListItemText>
                    </MenuItem>
                ) : null}
            </Menu>
            <OcAppBarHmiStudioSubMenu
                handleCloseAppBarMenu={handleClose}
                anchorHmiStudioEl={anchorHmiStudioEl}
                setAnchorHmiStudioEl={setAnchorHmiStudioEl}
            />
        </div>
    )
}
export const OcAppBarMenu = observer(_OcAppBarMenu)
